import { AppRoutes } from "../../routes/routes";
import { RegistrationChange } from "./registrations-service";
export class RegistrationsViewModel {
    store;
    service;
    name = this.constructor.name;
    constructor(store, service) {
        this.store = store;
        this.service = service;
    }
    // Getter props
    get registrations() {
        return this.store.registrations;
    }
    get selectedRegistration() {
        return this.store.selectedRegistration;
    }
    get hasHistory() {
        return (this.store.history ?? []).length > 0;
    }
    get history() {
        return this.store.history;
    }
    // Groups history entries into date buckets (newest first), sorting each bucket.
    // Exposed as a pure method so callers can memoize on the history reference.
    groupHistoryByDay(history) {
        if (history === null) {
            return null;
        }
        const historyBack = history.slice().reverse();
        // reverting history backward so it goes from new entry to the old
        const result = historyBack.reduce((grouped, entry) => {
            if (entry.timestamp) {
                // extract date
                const date = new Date(entry.timestamp).toISOString().split('T')[0];
                if (!grouped[date]) {
                    grouped[date] = [];
                }
                grouped[date].push(entry);
            }
            return grouped;
        }, {});
        // sort result in place
        Object.values(result).forEach((history) => history.sort(compareRegistrationHistory));
        return result;
    }
    get alsoInactive() {
        return this.store.alsoInactive;
    }
    get allRegistrationsLoaded() {
        return this.store.allEntriesLoaded;
    }
    get availableRegistrationChanges() {
        if (!this.store.selectedRegistration) {
            return [];
        }
        const reg = this.store.selectedRegistration;
        const changes = [];
        if (reg.registrationStatus === "ACTIVE") {
            changes.push(RegistrationChange.block);
        }
        if (reg.registrationStatus === "BLOCKED") {
            changes.push(RegistrationChange.unblock);
        }
        if (reg.registrationStatus !== "REMOVED") {
            changes.push(RegistrationChange.remove);
        }
        return changes;
    }
    // Actions
    setSelectedRegistration(reg) {
        this.store.setSelectedRegistration(reg);
    }
    setAlsoInactive(alsoInactive, userId) {
        this.store.setAlsoInactive(alsoInactive);
        void this.getRegistrations(userId);
    }
    // Service calls
    async changeRegistrationState(change, regId, userId, externalUserId, blockReason) {
        await this.service.changeRegistrationState(change, regId, externalUserId, blockReason);
        // fetch new state from backend and update accordingly
        if (regId) {
            await this.service.reloadRegistration(regId);
        }
        // also fetch all registrations
        await this.service.getRegistrationsList(userId, this.alsoInactive);
    }
    async reloadSelectedRegistration() {
        if (this.store.selectedRegistration?.registrationId) {
            await this.service.reloadRegistration(this.store.selectedRegistration.registrationId, true);
        }
    }
    async getRegistrations(userId, lonOnSuccess = false) {
        await this.service.getRegistrationsList(userId, this.alsoInactive, lonOnSuccess);
    }
    async getNextPage(userId) {
        if (!userId) {
            return;
        }
        else {
            await this.service.getNextPage(userId, this.alsoInactive);
        }
    }
    async deleteRegistration(regId, userId) {
        await this.service.deleteRegistration(regId);
        // reload registrations - the logic of sorting active/inactive registration is on backend, so we need
        // to do full reload
        await this.service.getRegistrationsList(userId, this.alsoInactive);
    }
    async getRegistrationHistory(regId) {
        if (!regId) {
            return;
        }
        await this.service.getRegistrationHistory(regId);
    }
    notifyLocationChanged(route, params) {
        // Registration List
        if (route === AppRoutes.Registrations && params?.userId) {
            // if there are no data, we want to do init call on route change
            if (this.store.registrations.length === 0) {
                void this.getRegistrations(params.userId);
            }
        }
        // Registration Detail
        if (route === AppRoutes.RegistrationsDetail) {
            // if there is no selected registration, it means user was not in registration list
            // it most probably re-loads the registration detail URL
            if (!this.selectedRegistration && params?.uuid) {
                // in that case, we want to fetch registration detail and set it as selected registration
                void this.service.reloadRegistration(params.uuid);
            }
        }
    }
}
function compareRegistrationHistory(a, b) {
    return new Date(a.timestamp ?? 0).getTime() - new Date(b.timestamp ?? 0).getTime();
}
